import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { BaseForm } from '../../../../base-form';
import { VisitasApiService } from '../../../../../service/api/visitas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { FormCommonApiService } from '../../../../../service/api/formcommon-api.service';
import { build, pool } from '@fitogest/reactive-form-builder/utils';

import { VisitasSemanaModel } from 'src/app/models/visitas/visitas-semana.model';
import { VisitasSemanaFormDefinition } from './form-control/form-definition';
import type {
    ReactiveFormChangedField,
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';

@Component({
    selector: 'app-visitas-semana-form',
    standalone: false,
    templateUrl: './visitas-semana-form.component.html',
    styleUrls: ['./visitas-semana-form.component.scss']
})
export class VisitasSemanaFormComponent extends BaseForm<VisitasSemanaModel> implements OnInit {
    public override model: VisitasSemanaModel = { semana: 0 };

    public form!: VisitasSemanaFormDefinition;

    public readonly reactiveFormLoadingLabel = 'Preparando formulario de visitas semana...';

    public isReactiveFormReady = false;

    public reactiveFormLoadingError = '';

    public initialPreviousData: VisitasSemanaModel | null = null;

    public reactiveFormRecordId = '';

    public constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public visitasApi: VisitasApiService,
        public fincasApi: FincasApiService,
        public formApi: FormCommonApiService,
        public override router: Router,
    ) {
        super(
            route,
            router,
            dashboard,
            visitasApi.visitasSemanaPUT,
            visitasApi.visitasSemanaPOST,
            'visitas-semana',
            'Actualizar Visita',
            'Crear Visita',
        );
    }// constructor();

    public ngOnInit(): void {
        void this.initializeVisitasSemanaForm();
    }// ngOnInit();

    private async initializeVisitasSemanaForm(): Promise<void> {
        try {
            this.isReactiveFormReady = false;
            this.reactiveFormLoadingError = '';
            this.resetExternalDropdowns();
            this.softInit(this.getType());
            const routeModel = this.toVisitasSemanaModel(this.getModel() as unknown as Record<string, unknown>);
            this.reactiveFormRecordId = this.resolveReactiveFormRecordId(routeModel);
            const initialModel = this.initializeHostModel({
                ...this.model,
                ...routeModel,
            });

            this.form = new VisitasSemanaFormDefinition(this.getType(), initialModel);
            this.initialPreviousData = this.getInitialPreviousData(routeModel);
            await this.initFormData();
            this.markReactiveFormReady();
        } catch (error: unknown) {
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            this.finalizeExternalDropdowns();
            console.error('No se ha podido preparar el formulario de visitas semana.', error);
        }
    }// initializeVisitasSemanaForm();

    public stableValueChanges(event: ReactiveFormStableValueChange<VisitasSemanaModel>): void {
        const changedFieldName = event.changedField;
        const stableValue = this.captureStableValueChange(event.value as VisitasSemanaModel);

        this.form.updateModel(stableValue, changedFieldName);
        this.syncModelFromForm();

        if (event.origin !== 'user') {
            return;
        }// if();

        this.handleUserValueChange(event.value, event.changedField);
        this.formBuilderRef?.updateForm(this.form.model as Record<string, unknown>, false);
    }// stableValueChanges();

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<VisitasSemanaModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged' && event.type !== 'dropdownRefresh') {
            return;
        }// if();

        const stableValue = this.captureStableValueChange(event.value as VisitasSemanaModel);

        this.form.updateModel(stableValue, event.changedField);
        this.syncModelFromForm();
    }// onReactiveFormLifecycleEvent();

    private handleUserValueChange(
        formModel: ReactiveFormValue<VisitasSemanaModel>,
        changedFieldName: ReactiveFormChangedField<VisitasSemanaModel>,
    ): void {
        this.form.updateModel(formModel, changedFieldName);
        this.syncModelFromForm();
        this.finalizeExternalDropdowns();
        this.refreshReactiveDropdownMappings();
    }// handleUserValueChange();

    private initFormData(): Promise<boolean> {
        return pool([
            build(this.fincasApi.fincasGET, this.form.lists.fincas, 'nombre'),
            build(this.fincasApi.parcelasGET, this.form.lists.parcelas, 'nombre', true),
        ]).then(() => true);
    }// initFormData();

    private markReactiveFormReady(): void {
        this.isReactiveFormReady = true;
        this.finalizeExternalDropdowns();
        this.refreshReactiveDropdownMappings();
    }// markReactiveFormReady();

    private syncModelFromForm(): void {
        const normalizedModel = this.toVisitasSemanaModel(this.form.model);

        this.form.model = normalizedModel;
        this.updateHostModel(normalizedModel);
    }// syncModelFromForm();

    private refreshReactiveDropdownMappings(): void {
        this.formBuilderRef?.requestExternalDropdownMappingsRefresh();
    }// refreshReactiveDropdownMappings();

    private getInitialPreviousData(routeModel: VisitasSemanaModel): VisitasSemanaModel | null {
        return Object.keys(routeModel as Record<string, unknown>).length === 0
            ? null
            : this.toVisitasSemanaModel(routeModel as unknown as Record<string, unknown>);
    }// getInitialPreviousData();

    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim() !== '') {
            return `No se ha podido cargar el formulario de visitas semana: ${error.message}`;
        }// if();

        return 'No se ha podido cargar el formulario de visitas semana. Revisa la conexión y vuelve a intentarlo.';
    }// getReadableInitialisationError();

    private toVisitasSemanaModel(
        value: Record<string, unknown> | ReactiveFormValue<VisitasSemanaModel> | VisitasSemanaModel,
    ): VisitasSemanaModel {
        return { ...(value as Record<string, unknown>) } as unknown as VisitasSemanaModel;
    }// toVisitasSemanaModel();
}
